import { useQuery } from '@tanstack/react-query';
import type { Dispatch, SetStateAction } from 'react';

import { CenterFlex } from '@/components/base';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import queryKeys from '@/configs/react-query-keys';
import service from '@/services/index';
import useFilterItemsStore from '@/stores/FilterItemsStore';
import useToastStore from '@/stores/pure/ToastStore';
import { ReanalyzeStatus } from '@/types/filterItems';

import FilterItemsEmail from './FilterItemsEmail';

type FilterItemsAnalysisEmailTabProps = {
  setHasEmailTabChange: Dispatch<SetStateAction<boolean>>;
};

const FilterItemsAnalysisEmailTab = (
  props: FilterItemsAnalysisEmailTabProps,
) => {
  const { reanalyzeStatus, startReanalyze } = useFilterItemsStore();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));
  const {
    data: filterItemsEmail,
    isLoading: isFilterItemsEmailLoading,
    isError: isFilterItemsEmailError,
  } = useQuery({
    queryKey: queryKeys.filterItems.FILTER_ITEMS_ANALYSIS_EMAIL,
    queryFn: service.filterItems.fetchAnalysisFilterEmails,
  });

  if (isFilterItemsEmailError) {
    addDefaultToast('Failed to fetch filter items', 'fail');
  }

  const onSaveChange = async ({
    addedEmails,
    deletedEmails,
    addedEmailDomains,
    deletedEmailDomains,
  }: {
    addedEmails: string[];
    deletedEmails: string[];
    addedEmailDomains: string[];
    deletedEmailDomains: string[];
  }) => {
    try {
      const decapitalizedAddedEmails = addedEmails.map((email) =>
        email.toLowerCase(),
      );
      const decapitalizedAddedEmailDomains = addedEmailDomains.map(
        (emailDomain) => emailDomain.toLowerCase(),
      );
      const decapitalizedDeletedEmails = deletedEmails.map((email) =>
        email.toLowerCase(),
      );
      const decapitalizedDeletedEmailDomains = deletedEmailDomains.map(
        (emailDomain) => emailDomain.toLowerCase(),
      );

      const data = await service.filterItems.changeAnalysisFilterEmails({
        added_emails: decapitalizedAddedEmails,
        deleted_emails: decapitalizedDeletedEmails,
        added_email_domains: decapitalizedAddedEmailDomains,
        deleted_email_domains: decapitalizedDeletedEmailDomains,
      });

      startReanalyze();

      return data;
    } catch (e) {
      throw e;
    }
  };

  return (
    <Transition
      status={!isFilterItemsEmailLoading}
      fallback={
        <CenterFlex height="100%">
          <Spinner />
        </CenterFlex>
      }
    >
      <FilterItemsEmail
        data={filterItemsEmail}
        onSaveChange={onSaveChange}
        setHasEmailTabChange={props.setHasEmailTabChange}
        editDisabled={
          reanalyzeStatus === ReanalyzeStatus.REANALYZE_STATUS_RUNNING
        }
      />
    </Transition>
  );
};

export default FilterItemsAnalysisEmailTab;
